/**
 * @file timerDisplay.ts
 * @brief Helpers for formatting active timer elapsed time in the UI.
 */

import { ActiveTimer } from '@/types';

/**
 * @brief Formats seconds as MM:SS or HH:MM:SS.
 * @param seconds Elapsed seconds.
 * @param includeHours When true, always uses HH:MM:SS.
 * @return Formatted duration string.
 */
export function formatTimerSeconds(seconds: number, includeHours = false): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hrs = Math.floor(safeSeconds / 3600);
  const mins = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  if (includeHours || hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * @brief Returns elapsed seconds for display from a timer state object.
 * @param timer Active timer record from Redux.
 * @return Elapsed seconds suitable for UI display.
 */
export function getTimerDisplaySeconds(timer: ActiveTimer): number {
  return Math.max(0, Math.floor(timer.accumulatedTime ?? 0));
}
